// Single source of truth for client-side upload size limits, so the Settings
// overview and the enforcing call sites can't drift apart.
//
// Every upload in the app is base64-inlined into a GraphQL mutation body and
// rides the BFF GraphQL proxy, which caps the request body at
// GRAPHQL_BODY_LIMIT (16 MB, see apps/bff/src/limits.ts). base64 inflates a
// payload by ~33 %, so the per-file caps below sit safely under that ceiling.

export const MB = 1024 * 1024;

/** BFF GraphQL body cap (apps/bff/src/limits.ts GRAPHQL_BODY_LIMIT). */
export const BFF_GRAPHQL_BODY_LIMIT = 16 * MB;

/** Save · Service document upload (enforced in ServicePage). */
export const MAX_DOCUMENT_UPLOAD_BYTES = 10 * MB;
/**
 * Documents per Power-Analyse run (ExtractionDialog). Not a byte cap: the dialog reads every added
 * file to base64 up front and holds it in state, so an unbounded N is an unbounded tab — and genesis
 * starts one parallel /preprocess call per selected document. genesis mirrors this in
 * `ExtractionDocumentLimits.MAX_DOCUMENTS_PER_RUN` and enforces it on the mutation, so this cap is
 * the early, friendly half, not the control.
 */
export const MAX_DOCUMENTS_PER_RUN = 10;

/** Share item cover image. */
export const MAX_SHARE_IMAGE_BYTES = 5 * MB;
/**
 * Share item attachment. The BFF body cap (16 MB) minus base64 inflation
 * (~33 %) and headroom for the rest of the mutation payload.
 */
export const MAX_SHARE_ATTACHMENT_BYTES = 11 * MB;
/** Profile photo / avatar. */
export const MAX_AVATAR_BYTES = 5 * MB;
/**
 * Pixel ceiling for the avatar, because bytes do not bound decode cost: a browser holds a decoded
 * bitmap at ~4 bytes per pixel, and a highly compressible image (20000x20000 of flat colour) sits far
 * under MAX_AVATAR_BYTES while needing gigabytes to decode — which freezes or OOM-kills the tab before
 * any byte check could matter. 64 MP leaves every consumer camera through (48 MP phones included) and
 * costs ~256 MB decoded.
 */
export const MAX_AVATAR_PIXELS = 64_000_000;
/**
 * Admin file import (BulkYamlImport / startFileImport). Client-side pre-check
 * and the number the UI promises; genesis stays the authority on everything it
 * alone can check (its own 25 MB job ceiling and the 1,000-entity cap, which
 * needs the YAML parsed).
 *
 * Deliberately *below* genesis's 25 MB: the file rides `startFileImport` as a
 * base64 `Binary!` through the `/graphql` proxy, which caps the whole request
 * body at GRAPHQL_BODY_LIMIT (16 MB, apps/bff/src/limits.ts). base64 inflates
 * by ~33 %, so anything past ~12 MB is rejected by the proxy with a generic
 * error before genesis ever sees it. Promising 25 MB would be promising a size
 * the transport cannot carry.
 *
 * Same transport, same reasoning, same number as MAX_SHARE_ATTACHMENT_BYTES
 * above — kept identical on purpose, so the two cannot drift into implying
 * different ceilings for one proxy limit. Raising this needs the transport
 * changed first (a bigger GraphQL body cap, or a dedicated multipart route
 * like the RAG upload's), not the constant.
 */
export const MAX_YAML_IMPORT_BYTES = 11 * MB;
/**
 * Questionnaire group tile image (Systemeinstellungen → Fragebögen). Same
 * ceiling as the share cover; genesis mirrors it in
 * QuestionnaireGroupController.MAX_IMAGE_BYTES and enforces it server-side, so
 * this cap is a courtesy to the editor, not the control.
 */
export const MAX_QUESTIONNAIRE_GROUP_IMAGE_BYTES = 5 * MB;
/**
 * Company logo (the /c/:id admin editor). genesis mirrors this ceiling in
 * `ImageContentValidation.MAX_IMAGE_BYTES` and enforces it on the DECODED
 * bytes, so this cap only spares the admin an upload — it is not the control.
 * The mutation is reachable with a token and no browser at all.
 */
export const MAX_COMPANY_LOGO_BYTES = 5 * MB;
/**
 * Platform logo (Systemeinstellungen, Branding): the file the admin may PICK. Same number as the
 * company logo, and for the same reason it is not the control: the pick goes through the cropper,
 * and what reaches genesis is the cropper's 640 by 160 PNG, never the upload. genesis keeps that
 * result INLINE in the single settings row (ADR GENESIS-0011), so its own ceiling there is far
 * lower (`ImageContentValidation.MAX_INLINE_IMAGE_BYTES`, 512 KiB) and is enforced on the cropped
 * bytes; a flat wordmark at that size is tens of kilobytes. This cap only spares the admin a file
 * the cropper would have to decode for nothing.
 */
export const MAX_BRAND_LOGO_BYTES = 5 * MB;

/**
 * Types a document or attachment slot accepts. Both slots accepted any type at all, and nothing downstream
 * checks the type — not the change handler (size only), not the BFF (byte pass-through), not genesis
 * (`createDocument` gates neither type nor size). The attachment is later opened via
 * `URL.createObjectURL`, and a blob: URL inherits spark's origin, so an HTML or SVG upload would run in
 * it. Office/PDF plus the raster image formats covers every real brief attachment.
 */
export const PROJECT_DOCUMENT_ACCEPT =
  "application/pdf,application/msword," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "text/plain,image/png,image/jpeg,image/gif,image/webp";

/** Types the CV extraction reads, for one CV (CV Extractor) and in bulk (BulkCvImport) alike. */
export const CV_DOCUMENT_ACCEPT = ".pdf,.doc,.docx,.txt,.rtf,.html,.png,.jpg,.jpeg";

/**
 * File types the knowledge-document (RAG) upload offers in its picker. The pipeline OCRs PDFs and
 * Tika-extracts text from Office/text formats; images become figures only for pdf/docx/pptx (see
 * figure-support.ts). Standalone image files are intentionally excluded — the pipeline has no bare-image
 * OCR path, so an image on its own would yield only metadata text. `accept` is a hint, not enforcement:
 * the agent-service backend deny-lists archives/executables regardless of what the picker allows.
 */
export const RAG_DOCUMENT_ACCEPT = [
  ".pdf", ".docx", ".pptx", ".xlsx", ".doc", ".ppt", ".xls",
  ".txt", ".csv", ".md", ".rtf", ".html", ".odt", ".odp", ".ods",
].join(",");

/**
 * Raster formats backdrop can derive the `card` variant from. Narrower than
 * `image/*` on purpose: SVG passes an `image/*` filter but genesis rejects it
 * by magic byte, and a tile or logo could never render an unscaled vector anyway.
 *
 * ONE list for every artwork field, mirroring genesis's own
 * `ImageContentValidation.isScalableRaster` — which exists as a single shared
 * class for exactly this reason: a copied table drifts, and the field that kept
 * its own copy then silently accepts or refuses something the others do not.
 * genesis decides on the bytes; this only spares the picker the wrong files.
 */
export const RASTER_IMAGE_ACCEPT =
  "image/png,image/jpeg,image/gif,image/bmp,image/tiff,image/webp";

/**
 * Bytes as the UI names them. The one place a byte count becomes a shown
 * number, so a limit and a file measured against it can never be rendered on
 * different scales. Whole megabytes drop the decimal ("11 MB"), because a cap
 * is a round promise; a measured file keeps it ("12.4 MB").
 */
export function formatMegabytes(bytes: number): string {
  const mb = bytes / MB;
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}

export interface UploadLimit {
  /** i18n id: where in the app this applies. */
  areaId: string;
  /** i18n id: what is uploaded there. */
  whatId: string;
  /** i18n id: human-readable limit. */
  limitId: string;
  /** i18n id: optional clarification. */
  noteId?: string;
}

// Display list for the Settings overview. Keep in sync with the constants
// above and the enforcing components. Strings live in i18n under
// settings.upload.*; resolved at render in SettingsPage.
export const UPLOAD_LIMITS: readonly UploadLimit[] = [
  {
    areaId: "settings.upload.docs.area",
    whatId: "settings.upload.docs.what",
    limitId: "settings.upload.docs.limit",
  },
  {
    areaId: "settings.upload.shareImage.area",
    whatId: "settings.upload.shareImage.what",
    limitId: "settings.upload.shareImage.limit",
  },
  {
    areaId: "settings.upload.shareAttach.area",
    whatId: "settings.upload.shareAttach.what",
    limitId: "settings.upload.shareAttach.limit",
    noteId: "settings.upload.shareAttach.note",
  },
  {
    areaId: "settings.upload.profile.area",
    whatId: "settings.upload.profile.what",
    limitId: "settings.upload.profile.limit",
  },
  {
    areaId: "settings.upload.appearance.area",
    whatId: "settings.upload.appearance.what",
    limitId: "settings.upload.appearance.limit",
    noteId: "settings.upload.appearance.note",
  },
];

/** i18n id for the general footnote under the upload-limits overview. */
export const UPLOAD_LIMIT_NOTE_ID = "settings.upload.generalNote";
